import endpoints from "../../../../utils/endpoints";
import type { ApiRequestSpec, ApiResult } from "../types";
import { FlowStepError } from "./errors";

type ApiFn = <T = any>(spec: ApiRequestSpec) => Promise<ApiResult<T>>;

export interface SupplierSession {
  supplierId: string;
  mobileNumber: string;
  token: string;
  userId?: string;
  userName?: string;
  method: "proxy" | "otp";
  obtainedAt: number;
}

const SESSION_KEY = "ORCA_FLOW_TESTER_SUPPLIER_SESSIONS";
const SECRET_KEY = "ORCA_FLOW_TESTER_SUPPLIER_SECRET";
const MOBILE_PREFIX = "91";

const memory = new Map<string, SupplierSession>();

const readStore = (): Record<string, SupplierSession> => {
  try {
    return JSON.parse(sessionStorage.getItem(SESSION_KEY) || "{}");
  } catch {
    return {};
  }
};

const writeStore = (store: Record<string, SupplierSession>) => {
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(store));
};

export const getSupplierSession = (supplierId: string): SupplierSession | undefined => {
  if (memory.has(supplierId)) return memory.get(supplierId);
  const stored = readStore()[supplierId];
  if (stored) memory.set(supplierId, stored);
  return stored;
};

export const saveSupplierSession = (session: SupplierSession) => {
  memory.set(session.supplierId, session);
  const store = readStore();
  store[session.supplierId] = session;
  writeStore(store);
};

export const clearSupplierSession = (supplierId?: string) => {
  if (supplierId) {
    memory.delete(supplierId);
    const store = readStore();
    delete store[supplierId];
    writeStore(store);
  } else {
    memory.clear();
    sessionStorage.removeItem(SESSION_KEY);
  }
};

const readLocalSecret = (): string => {
  try {
    return localStorage.getItem(SECRET_KEY) || "";
  } catch {
    return "";
  }
};

/** Persist the proxy-login secret on this device. Never read from env. */
export const setSupplierLoginSecret = (secret: string) => {
  try {
    if (secret) localStorage.setItem(SECRET_KEY, secret);
    else localStorage.removeItem(SECRET_KEY);
  } catch {
    /* private mode / blocked storage */
  }
};

/** Secret is asked once and stored in localStorage; never taken from env. */
export const getSupplierLoginSecret = (): string => {
  const local = readLocalSecret();
  if (local) return local;
  try {
    const legacy = sessionStorage.getItem(SECRET_KEY);
    if (legacy) {
      setSupplierLoginSecret(legacy);
      sessionStorage.removeItem(SECRET_KEY);
      return legacy;
    }
  } catch {
    /* ignore */
  }
  return "";
};

/** @deprecated use setSupplierLoginSecret — kept so existing tests keep compiling */
export const setSupplierLoginSecretForSession = setSupplierLoginSecret;

export const normalizeMobile = (mobile: string) => {
  const digits = String(mobile || "").replace(/\D/g, "");
  return digits.length > 10 ? digits.slice(-10) : digits;
};

const isSupplierWebAppRole = (roles: any[]) =>
  (roles || []).some(
    (r) =>
      (r?.component === "SUPPLIER_WEB_APP" || r?.component === "SUPPLIER") &&
      ["ADMIN", "SUPER_ADMIN", "EMPLOYEE"].includes(r?.role)
  );

export class SupplierAuthError extends FlowStepError {
  code: "NO_SECRET" | "ROLE_MISSING" | "UNAUTHORIZED" | "NO_MOBILE" | "OTHER";
  constructor(
    message: string,
    code: SupplierAuthError["code"],
    opts: ConstructorParameters<typeof FlowStepError>[1] = {}
  ) {
    super(message, opts);
    this.name = "SupplierAuthError";
    this.code = code;
  }
}

/**
 * Logs in as the supplier using the same proxy endpoint the distributor app
 * uses for grazeon-assisted login. The caller's OrCA JWT must carry a
 * SUPPLIER_WEB_APP admin role; see `enableSupplierRoleForCurrentUser`.
 */
export const proxyLoginSupplier = async (args: {
  api: ApiFn;
  supplierId: string;
  mobileNumber: string;
  secret?: string;
}): Promise<SupplierSession> => {
  const secret = args.secret || getSupplierLoginSecret();
  const mobile = normalizeMobile(args.mobileNumber);
  if (!mobile) {
    throw new SupplierAuthError(
      "Supplier has no mobile number on record; cannot obtain a supplier session.",
      "NO_MOBILE"
    );
  }
  if (!secret) {
    throw new SupplierAuthError(
      "Supplier proxy-login secret is not saved yet. Enter it when prompted (it is stored in this browser’s localStorage).",
      "NO_SECRET"
    );
  }

  try {
    const res = await args.api<{ user: any; auth: string }>({
      label: "Supplier proxy login",
      method: "GET",
      path: `/otp/verifyProxyUser?mobileNumber=${MOBILE_PREFIX}${mobile}&secretKey=${encodeURIComponent(
        secret
      )}&role=SUPPLIER`,
      actor: "SUPPLIER",
      redactResponseKeys: ["auth"],
      redactQueryKeys: ["secretKey"],
    });
    const { user, auth } = res.data || ({} as any);
    if (!auth) {
      throw new SupplierAuthError("Proxy login returned no auth token", "OTHER", {
        apiCall: res.record,
      });
    }
    if (!isSupplierWebAppRole(user?.roles)) {
      throw new SupplierAuthError(
        "Supplier user does not have a SUPPLIER_WEB_APP role; supplier actions would be rejected.",
        "UNAUTHORIZED",
        { apiCall: res.record }
      );
    }
    const session: SupplierSession = {
      supplierId: args.supplierId,
      mobileNumber: mobile,
      token: auth,
      userId: user?._id,
      userName: [user?.firstName, user?.lastName].filter(Boolean).join(" "),
      method: "proxy",
      obtainedAt: Date.now(),
    };
    saveSupplierSession(session);
    return session;
  } catch (err: any) {
    if (err instanceof SupplierAuthError) throw err;
    const status = err?.apiCall?.status;
    if (status === 403) {
      throw new SupplierAuthError(
        "Your OrCA user lacks the SUPPLIER_WEB_APP admin role required for proxy login. Use “Enable supplier role” (one-time OTP on your own number) or log in as the supplier via OTP.",
        "ROLE_MISSING",
        { apiCall: err.apiCall }
      );
    }
    if (status === 401) {
      throw new SupplierAuthError(
        "Proxy login rejected (401): the secret key is wrong or the supplier user does not exist.",
        "UNAUTHORIZED",
        { apiCall: err.apiCall }
      );
    }
    throw new SupplierAuthError(err?.message || "Supplier proxy login failed", "OTHER", {
      apiCall: err?.apiCall,
    });
  }
};

export const requestOtp = async (api: ApiFn, mobileNumber: string) => {
  const mobile = normalizeMobile(mobileNumber);
  const visit = await api<{ visitId: string }>({
    label: "Create visit",
    method: "POST",
    path: endpoints.visitId,
    body: { source: "WEB" },
    authToken: "",
  });
  return api<{ message: string }>({
    label: "Request OTP",
    method: "GET",
    path: endpoints.getOTP(visit.data.visitId, mobile, MOBILE_PREFIX),
    authToken: "",
  });
};

/**
 * Verifies an OTP with role=SUPPLIER. For the OrCA user's own number this
 * grants the SUPPLIER_WEB_APP role needed by `verifyProxyUser` (one-time). For
 * a supplier's number it yields a supplier token directly (manual fallback).
 */
export const verifyOtpAsSupplier = async (
  api: ApiFn,
  mobileNumber: string,
  otp: string
) => {
  const mobile = normalizeMobile(mobileNumber);
  return api<{ auth: string; user: any }>({
    label: "Verify OTP (role=SUPPLIER)",
    method: "GET",
    path: `/otp/verify?mobileNumber=${MOBILE_PREFIX}${mobile}&otp=${encodeURIComponent(
      otp
    )}&role=SUPPLIER`,
    actor: "SUPPLIER",
    authToken: "",
    redactResponseKeys: ["auth"],
    redactQueryKeys: ["otp"],
  });
};

export const saveManualSupplierSession = (args: {
  supplierId: string;
  mobileNumber: string;
  token: string;
  user?: any;
}) => {
  const session: SupplierSession = {
    supplierId: args.supplierId,
    mobileNumber: normalizeMobile(args.mobileNumber),
    token: args.token,
    userId: args.user?._id,
    userName: [args.user?.firstName, args.user?.lastName].filter(Boolean).join(" "),
    method: "otp",
    obtainedAt: Date.now(),
  };
  saveSupplierSession(session);
  return session;
};
